import {
    COMMON_ADVANCED_SETTINGS_PAGE_KEYS,
    ADVANCED_SETTINGS_WYSIWYG_TOOLBAR_ITEM_KEYS,
} from '@lumapps/customer-settings/constants';
import { INSTANCE } from '@lumapps/instance/keys';
import { GLOBAL } from '@lumapps/translations';

import { INSTANCE_SETTINGS } from './keys';

export const SLUG_EXISTS_ERROR = 'ADMIN_INSTANCE_SLUG_EXIST';

export const SETTINGS_FORM_SCOPE = 'site-settings-form';

export const SETTINGS_PAGE_ID = 'SITE_GLOBAL_SETTINGS';
export const ADVANCED_SETTINGS_PAGE_ID = 'SITE_ADVANCED_SETTINGS';

/**
 * Named translation keys used directly with translateKey() in the instance's SettingsPage and its children.
 * Adds the instance-specific page header keys on top of the shared fieldset keys.
 *
 * /!\ When adding a new translateKey() call to SettingsPage (instance) or any of its descendants, add the key here first.
 */
export const INSTANCE_GLOBAL_SETTINGS_PAGE_KEYS = {
    // From SettingsPage header
    GLOBAL_SETTINGS: INSTANCE_SETTINGS.GLOBAL_SETTINGS,
    INSTANCE_SETTINGS: INSTANCE.INSTANCE_SETTINGS,

    // From ConfigurationFieldset
    GENERAL: GLOBAL.GENERAL,
    SITE_NAME: INSTANCE_SETTINGS.SITE_NAME,
    URL: GLOBAL.URL,
    PARENT_SITE: INSTANCE_SETTINGS.PARENT_SITE,
    TITLE: GLOBAL.TITLE,
    SUBTITLE: GLOBAL.SUBTITLE,
    DEFAULT_INSTANCE: INSTANCE_SETTINGS.DEFAULT_INSTANCE,
    PUBLIC_CONTENT: INSTANCE_SETTINGS.PUBLIC_CONTENT,

    // From FormInstanceLogo / InstanceLogo
    LOGO: INSTANCE.LOGO,
    UPLOAD_LOGO: INSTANCE.UPLOAD_LOGO,

    // From FormInstanceFavIcon / InstanceFavIcon
    FAVICON: INSTANCE.FAVICON,
    UPLOAD_FAVICON: INSTANCE.UPLOAD_FAVICON,

    // From LanguagesFieldset
    LANGUAGES: GLOBAL.LANGUAGES,
    DEFAULT_LANGUAGE: INSTANCE_SETTINGS.DEFAULT_LANGUAGE,

    // From VisibilityFieldset
    VISIBILITY: GLOBAL.VISIBILITY,
    RESTRICT_TO: INSTANCE_SETTINGS.RESTRICT_TO,
    IN_FAVORITE_FEEDS: INSTANCE_SETTINGS.IN_FAVORITE_FEEDS,
    IS_DELETABLE_FROM_FAVORITE: INSTANCE_SETTINGS.IS_DELETABLE_FROM_FAVORITE,

    // From TrackingFieldset
    ANALYTICS: GLOBAL.ANALYTICS,
    GOOGLE_ANALYTICS: GLOBAL.GOOGLE_ANALYTICS,
    INVALID_GOOGLE_ANALYTICS: INSTANCE_SETTINGS.INVALID_GOOGLE_ANALYTICS,
    GOOGLE_ANALYTICS_HELPER: INSTANCE_SETTINGS.GOOGLE_ANALYTICS_HELPER,
    GOOGLE_TAG_MANAGER: INSTANCE_SETTINGS.GOOGLE_TAG_MANAGER,
    INVALID_GOOGLE_TAG_MANAGER: INSTANCE_SETTINGS.INVALID_GOOGLE_TAG_MANAGER,

    // From LegacyCustomizationsFieldset
    INSTANCE_THEME: INSTANCE_SETTINGS.INSTANCE_THEME,
    INSTANCE_CONFIGURATION: INSTANCE_SETTINGS.INSTANCE_CONFIGURATION,
    INSTANCE_OVERRIDE: INSTANCE_SETTINGS.INSTANCE_OVERRIDE,

    // From SiblingCredentialsFieldset
    SEARCH: GLOBAL.SEARCH,
    SEARCH_PARENT_LIST_CHILDREN: INSTANCE_SETTINGS.SEARCH_PARENT_LIST_CHILDREN,
    SEARCH_CHILDREN_LIST_PARENT: INSTANCE_SETTINGS.SEARCH_CHILDREN_LIST_PARENT,
    SEARCH_CHILDREN_LIST_CHILDREN: INSTANCE_SETTINGS.SEARCH_CHILDREN_LIST_CHILDREN,

    // From SettingsForm (Metadata section + error message)
    SITE_METADATA: INSTANCE_SETTINGS.SITE_METADATA,
} as const;

export const INSTANCE_GLOBAL_SETTINGS_ALL_PAGE_KEYS = Object.values(INSTANCE_GLOBAL_SETTINGS_PAGE_KEYS);

/**
 * Named translation keys used directly with translateKey() in the instance's AdvancedSettingsPage.
 * Adds the instance-specific page header keys on top of the shared fieldset keys.
 *
 * /!\ When adding a new translateKey() call to AdvancedSettingsPage (instance), add the key here first.
 */
export const INSTANCE_ADVANCED_SETTINGS_PAGE_KEYS = {
    // From AdvancedSettingsPage UI (instance-specific)
    INSTANCE_SETTINGS: INSTANCE.INSTANCE_SETTINGS,
    ADVANCED_SETTINGS: INSTANCE_SETTINGS.ADVANCED_SETTINGS,
    ...COMMON_ADVANCED_SETTINGS_PAGE_KEYS,
} as const;

/**
 * Complete set of all translation keys used across the instance Advanced Settings page (for Spotlight / pageKeys).
 * Combines the statically-named keys with the dynamically-generated WYSIWYG toolbar item keys.
 */
export const INSTANCE_ADVANCED_SETTINGS_ALL_PAGE_KEYS = [
    ...Object.values(INSTANCE_ADVANCED_SETTINGS_PAGE_KEYS),
    ...ADVANCED_SETTINGS_WYSIWYG_TOOLBAR_ITEM_KEYS,
];
